<template>
  <div class="emoji" :class="{ 'on-apple-device': onAppleDevice }">
    <svg v-if="glow" class="emoji-glow" viewBox="0 0 42 42">
      <defs>
        <radialGradient :id="gradientId" cx="50%" cy="50%" r="50%">
          <stop stop-color="currentColor" offset="50%" />
          <stop stop-color="currentColor" stop-opacity="0" offset="100%" />
        </radialGradient>
      </defs>
      <path id="path" :d="getGlowPath()" :fill="`url(#${gradientId})`"></path>
    </svg>
    <span class="emoji-span">{{ emoji }}</span>
  </div>
</template>

<script>
import { createUniqueId } from '@/utils/uuid'
import { isOnAppleDevice } from '@/utils/device'

export default {
  name: 'PublicUserEmoji',
  props: {
    emoji: {
      type: String,
    },
    glow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      gradientId: createUniqueId('svg-gradient'),
      onAppleDevice: isOnAppleDevice(),
    }
  },
  methods: {
    /**
     * Returns the SVG path for the glow effect.
     * This is placed in a method to make sure it's not generated
     * for public user emojis without glow where it's not needed.
     * @returns {string}
     */
    getGlowPath() {
      let tips = 7
      let svgPath = []
      const r = 21
      const angle = 360 / tips

      svgPath.push(`M${r} ${r}`)

      while (tips--) {
        const tipAngle = tips * angle
        const tipAngle2 = tips * angle + angle / 2

        const tipX = r + r * Math.cos((-tipAngle * Math.PI) / 180)
        const tipY = r + r * Math.sin((-tipAngle * Math.PI) / 180)

        const tipX2 = r + r * Math.cos((-tipAngle2 * Math.PI) / 180)
        const tipY2 = r + r * Math.sin((-tipAngle2 * Math.PI) / 180)

        svgPath.push(`L${tipX} ${tipY}`)
        svgPath.push(`A${r} ${r} 0 0 0 ${tipX2} ${tipY2}`)
        svgPath.push(`L${r} ${r}`)
      }

      return svgPath.join(' ')
    },
  },
}
</script>

<style lang="scss" scoped>
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.emoji {
  --emoji-glow-size: 3.5em;
  --emoji-border-color: #{$violet};
  --emoji-border-width: 0.08em;
  --emoji-background-color: #{$brightYellowPale};
  --emoji-circle-size: 2em;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: var(--emoji-circle-size);
  width: var(--emoji-circle-size);
  padding: 0.1em;
  vertical-align: middle;
  font-size: 2em;
  line-height: 0;
  border-radius: 100%;
  border: var(--emoji-border-width, 0.08em) solid
    var(--emoji-border-color, $violet);
  background: var(--emoji-background-color, $brightYellowPale);
}

.emoji-glow {
  position: absolute;
  display: block;
  width: var(--emoji-glow-size);
  height: var(--emoji-glow-size);
  color: var(--emoji-background-color);
  animation: rotate 60s linear infinite;
  z-index: 1;
  pointer-events: none;

  path {
    stroke-width: 0;
  }
}

.emoji-span {
  display: block;
  height: 1em;
  line-height: 1.125em;
  z-index: 2;

  &.on-apple-device {
    line-height: 1em;
  }
}
</style>
